import { useEffect, useRef, useState } from 'react';
import { Mail, Newspaper } from 'lucide-react';
import { useActiveConversation } from '../../../chat/ActiveConversationContext';
import { useTranslation } from '../../../../lib/i18n';
import { draftFileName, draftHeadline, draftReadOnlyHtml, draftReviewHtml, draftSpaceSlug, draftViewHtml, type DraftPreviewCardData } from '../../../../lib/draft-preview';
import { fetchDraftPreview, useDraftInlinePreview, useDraftState, type DraftStatePoll } from '../../../../lib/draft-preview-api';
import { fetchBlogDraftPreview, useBlogDraftInlinePreview } from '../../../../lib/blog-draft-preview-api';
import { blogDraftTitle, isSitePage } from '../../../../lib/blog-draft-preview';
import { useIsPhone } from '../../../../lib/use-is-phone';
import { useBlogDraftPreviewEnabled } from '../../../../lib/blog-draft-preview-flag';
import { useEmailPreviewWidgetEnabled } from '../../../../lib/email-preview-widget-flag';
import { useSocialPostDraftsEnabled } from '../../../../lib/social-post-drafts-flag';
import { openDraftReview, type SendDraftComments } from '../../../../lib/draft-review-store';
import { openDocViewer } from '../../../../lib/doc-viewer-presenter';
import { useMarketingPublishTimeEnabled } from '../../../../lib/marketing-publish-time-flag';
import { reviewNonce, reviewedViewDocument, siteViewDocument } from '../../../../lib/email-review';
import type { SentReviewComment } from '../../../../lib/site-review';
import { useChatOverlayOpen } from '../../../../lib/use-chat-overlay';
import { onChatTurnFinished } from '../../../../lib/site-edit-store';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useSitePaneFits } from '../../../../lib/use-site-pane';
import { useWorkspaces } from '../../../../lib/workspaces';
import { Card, Typography } from '../../ui';
import { BlogDraftInlinePreview } from './BlogDraftInlinePreview';
import { DraftInlinePreview } from './DraftInlinePreview';
import { InlineHtmlPreview } from './InlineHtmlPreview';
import { useReviewedViewLabels } from './EmailReviewMessage';
import { DraftActions } from './DraftActions';
import { SocialDraftPreviewCard } from './SocialDraftPreviewCard';
import styles from './DraftPreviewCard.module.css';

const BLOG_PHONE_WIDTH = 375;

interface DraftPreviewCardProps {
  card: DraftPreviewCardData;
  conversationId?: string;
  cardKey?: string;
  latest?: boolean;
  canSend?: boolean;
  held?: boolean;
  sendComments?: SendDraftComments;
  pastComments?: readonly SentReviewComment[];
  biPage?: string;
  onReviewOpened?: () => void;
  onEditFirst?: () => void;
  flush?: boolean;
}

export function DraftPreviewCard(props: DraftPreviewCardProps) {
  const emailEnabled = useEmailPreviewWidgetEnabled();
  const blogEnabled = useBlogDraftPreviewEnabled();
  const socialEnabled = useSocialPostDraftsEnabled();
  const { card } = props;
  const enabled = card.kind === 'EMAIL' ? emailEnabled : card.kind === 'SOCIAL_POST' ? socialEnabled : blogEnabled;
  if (enabled !== true) return null;
  if (card.kind === 'SOCIAL_POST') return card.post ? <SocialDraftPreviewCard {...props} card={{ ...card, post: card.post }} /> : null;
  return <DraftPreviewCardBody {...props} />;
}

function DraftPreviewCardBody({
  card,
  conversationId,
  cardKey,
  latest = true,
  canSend = true,
  held = false,
  sendComments,
  pastComments,
  biPage = 'chat',
  onReviewOpened,
  flush = false,
}: DraftPreviewCardProps) {
  const { t } = useTranslation();
  const email = card.kind === 'EMAIL';
  const copy = email ? 'emailReview' : 'blogDraft';
  const reviewedLabels = useReviewedViewLabels(email ? undefined : t('blogDraft.sent.title'));
  const { logPageClickBi } = useGenericBi();
  const workspaces = useWorkspaces();
  const spaceSlug = workspaces.status === 'ready' ? draftSpaceSlug(card, workspaces.workspaces) : card.spaceSlug;
  const paneFits = useSitePaneFits();
  const phone = useIsPhone();
  const chatOpen = useChatOverlayOpen();
  const { activeConversationId } = useActiveConversation();
  const beside = paneFits && chatOpen && conversationId != null && conversationId === activeConversationId;
  const inlineKey = cardKey ?? conversationId ?? '';
  const ids = { workspaceId: card.workspaceId, taskId: card.taskId, draftId: card.draftId, kind: card.kind };
  const [poll, setPoll] = useState<DraftStatePoll | null>(null);
  const taskState = useDraftState(ids, latest, poll);
  const stateRef = useRef(taskState.data);
  stateRef.current = taskState.data;
  useEffect(() => {
    if (!latest || !conversationId) return undefined;
    return onChatTurnFinished((id) => {
      const state = stateRef.current;
      const done = !!state?.delivered && !state.delivered.scheduled && !state.delivered.changesNotLive;
      if (id !== conversationId || state?.ended || done) return;
      setPoll({ at: Date.now(), deliveredAtTap: state?.delivered ?? null, scheduledAtTap: state?.scheduled?.at ?? null });
    });
  }, [latest, conversationId]);
  const wixRead = useBlogDraftInlinePreview(
    { workspaceId: card.workspaceId, metaSiteId: taskState.data?.wixPreview?.siteId ?? '', draftPostId: taskState.data?.wixPreview?.draftPostId ?? '' },
    inlineKey,
    false,
  );
  const wixPreview = wixRead.isError ? null : (taskState.data?.wixPreview ?? null);
  const ownRead = useDraftInlinePreview(ids, inlineKey, !email && latest && !taskState.isLoading);
  const headline = email ? draftHeadline(card) : ownRead.data ? draftHeadline(card, ownRead.data) : (wixPreview && wixRead.data ? blogDraftTitle(wixRead.data, '') : '') || draftHeadline(card);
  const publishTime = useMarketingPublishTimeEnabled() === true;
  const wentOut = publishTime && !!taskState.data?.delivered && !taskState.data.delivered.scheduled;
  const [state, setState] = useState<'idle' | 'loading' | 'unavailable'>('idle');
  const [revising, setRevising] = useState(false);
  const sawWorking = useRef(false);
  const working = taskState.data?.working === true;
  useEffect(() => {
    if (!revising) return;
    if (working) sawWorking.current = true;
    else if (sawWorking.current) {
      sawWorking.current = false;
      setRevising(false);
    }
  }, [revising, working]);
  const mounted = useRef(true);
  const visible = useRef(false);
  visible.current = conversationId == null || (chatOpen && conversationId === activeConversationId);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const review = () => {
    if (!spaceSlug || !canSend || !sendComments) return;
    const sendReview: SendDraftComments = (message, source, draftAction) => {
      const result = sendComments(message, source, draftAction);
      if (result.status === 'sent' && source !== 'tap') {
        setRevising(true);
        setPoll({ at: Date.now(), deliveredAtTap: taskState.data?.delivered ?? null, scheduledAtTap: taskState.data?.scheduled?.at ?? null });
      }
      return result;
    };
    openDraftReview({ card, spaceSlug, conversationId: conversationId ?? null, sendComments: sendReview, beside, wixPreview, revision: inlineKey });
    onReviewOpened?.();
  };

  const view = async () => {
    logPageClickBi({ page_name: biPage, button_name: 'draft_preview_view' }, { additionalFields: { kind: card.kind } });
    setState('loading');
    try {
      const site = !email && wixPreview && !pastComments?.length ? wixPreview : null;
      const [preview, sitePage] = await Promise.all([
        fetchDraftPreview(card.workspaceId, card.taskId, card.draftId, card.kind),
        site
          ? fetchBlogDraftPreview(card.workspaceId, site.siteId, site.draftPostId, phone ? 'mobile' : 'desktop').then(
              (read) => (isSitePage(read) ? siteViewDocument(read.html, reviewNonce()) : null),
              () => null,
            )
          : Promise.resolve(null),
      ]);
      if (!mounted.current || !visible.current) {
        if (mounted.current) setState('idle');
        return;
      }
      const title = draftHeadline(card, preview);
      openDocViewer({
        file: {
          name: draftFileName(card, title),
          isApp: false,
          content: pastComments?.length
            ? reviewedViewDocument(draftReviewHtml(card, preview), pastComments, reviewedLabels(pastComments), reviewNonce())
            : draftViewHtml(card, preview),
        },
        kind: 'html',
        pageName: 'document_viewer',
        besideChat: conversationId != null && (email ? beside : true),
        title: title || undefined,
        localOnly: true,
        copyText: preview.html,
        draftDoc: true,
        viewText: sitePage ?? (pastComments?.length ? undefined : draftReadOnlyHtml(card, preview)),
      });
      if (mounted.current) setState('idle');
    } catch {
      if (mounted.current) setState('unavailable');
    }
  };

  return (
    <Card variant="sunken" size="sm" disablePadding={flush} className={flush ? `${styles.card} ${styles.flush}` : styles.card} data-testid="chat-draft-preview-card">
      <button
        type="button"
        className={`${styles.row} ${styles.rowTap}`}
        onClick={() => void view()}
        aria-busy={state === 'loading'}
        data-testid="draftPreviewOpenHead"
      >
        <span className={styles.glyph} aria-hidden>
          {email ? <Mail size={18} /> : <Newspaper size={18} />}
        </span>
        <span className={styles.text}>
          <Typography variant="p3" color="secondary">
            {t(wentOut ? `${copy}.card.titleDone` : `${copy}.card.title`)}
          </Typography>
          {headline && (
            <Typography variant="p2" color="primary" className={styles.title}>
              {headline}
            </Typography>
          )}
          {state === 'unavailable' && (
            <Typography variant="p3" color="secondary" role="alert">
              {t(`${copy}.unavailable`)}
            </Typography>
          )}
        </span>
      </button>
      {latest && (
        <div className={styles.previewTap}>
          {email ? (
            <DraftInlinePreview card={card} cardKey={inlineKey} />
          ) : taskState.isLoading ? (
            <InlineHtmlPreview title={headline || t('blogDraft.card.title')} naturalWidth={BLOG_PHONE_WIDTH} status={t('blogDraft.loading')} testId="draftInlinePreview" />
          ) : wixPreview ? (
            <BlogDraftInlinePreview workspaceId={card.workspaceId} card={{ metaSiteId: wixPreview.siteId, draftPostId: wixPreview.draftPostId, title: card.title }} cardKey={inlineKey} />
          ) : (
            <DraftInlinePreview card={card} cardKey={inlineKey} />
          )}
          <button
            type="button"
            className={styles.previewTapCover}
            onClick={() => void view()}
            aria-label={t(`${copy}.card.view`)}
            data-testid="draftPreviewOpen"
          />
        </div>
      )}
      {latest && !held && (
        <DraftActions
          card={card}
          state={taskState.data}
          spaceSlug={spaceSlug}
          canSend={canSend}
          sendComments={sendComments}
          biPage={biPage}
          revising={revising}
          publishTime={publishTime}
          onEditFirst={review}
          onSent={() => setPoll({ at: Date.now(), deliveredAtTap: taskState.data?.delivered ?? null, scheduledAtTap: taskState.data?.scheduled?.at ?? null })}
        />
      )}
    </Card>
  );
}
